import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { CssBaseline, ThemeProvider, createTheme, alpha } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AuthProvider } from './context/AuthContext';
import { BrowserRouter } from 'react-router-dom';

// Define the custom theme with the provided palette
let ktTheme = createTheme({
  palette: {
    primary: {
      main: '#E94F37', // App orange for stronger contrast
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#5F7367', // Move greenish-grey to secondary
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#D7263D', // Slightly deeper error red
    },
    warning: {
      main: '#FFB020',
      contrastText: alpha('#000000', 0.87)
    },
    info: {
        main: '#1473E6',
    },
    success: {
        main: '#2E7D32',
    },
    background: {
      default: '#f4f6f8',
      paper: '#ffffff',
    },
    text: {
      primary: alpha('#000000', 0.87),
      secondary: alpha('#000000', 0.6),
      disabled: alpha('#000000', 0.38),
    },
    action: {
      active: '#E94F37',
      hover: alpha('#E94F37', 0.08),
      selected: alpha('#E94F37', 0.16),
      disabled: alpha('#000000', 0.26),
      disabledBackground: alpha('#000000', 0.12),
      focus: alpha('#E94F37', 0.12)
    }
  },
  typography: {
    // Optionally customize font family, sizes etc.
    // fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 500,
    },
    h5: {
      fontWeight: 500,
    },
    h6: {
        fontWeight: 500,
    }
  },
  shape: {
    borderRadius: 8, // Slightly more rounded corners
  },
  components: {
    // Global style overrides
    MuiAppBar: {
        styleOverrides: {
            root: ({
                // Use default background if needed, or primary
                // backgroundColor: '#ffffff', 
                // color: alpha('#000000', 0.87),
            })
        }
    },
    MuiDrawer: {
        styleOverrides: {
            paper: ({
                // backgroundColor: '#ffffff', // Ensure drawer background is white
                 borderRight: 'none', // Remove border if desired
            })
        }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none', // Prevent ALL CAPS buttons
        },
        containedPrimary: {
            // Example: Add subtle shadow
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            '&:hover': {
                 boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
            }
        }
      },
    },
    MuiPaper: {
        styleOverrides: {
            root: {
                // Add subtle shadow to paper elements like Tables
                // boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }
        }
    }
    // Add more component overrides as needed
  }
});

// Augment with responsive ListItem overrides and nav states
ktTheme = createTheme(ktTheme, {
  components: {
    MuiListItemButton: {
      styleOverrides: {
        root: {
          position: 'relative',
          minHeight: 44,
          paddingLeft: 12,
          paddingRight: 12,
          borderRadius: ktTheme.shape.borderRadius,
          transition: 'all 0.2s ease',
          [ktTheme.breakpoints.up('md')]: {
            minHeight: 50,
            paddingLeft: 16,
            paddingRight: 16
          },
          '&:hover': {
            backgroundColor: alpha(ktTheme.palette.text.primary, 0.04)
          },
          '&.Mui-selected': {
            color: ktTheme.palette.primary.main,
            backgroundColor: alpha(ktTheme.palette.primary.main, 0.08)
          },
          '&.Mui-selected::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 6,
            bottom: 6,
            width: 3,
            borderRadius: 2,
            backgroundColor: ktTheme.palette.primary.main
          }
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 36,
          color: 'inherit'
        }
      }
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '0.95rem',
          lineHeight: 1.3
        }
      }
    }
  }
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <BrowserRouter>
    <ThemeProvider theme={ktTheme}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
      <AuthProvider>
        <CssBaseline /> 
        <App />
      </AuthProvider>
      </LocalizationProvider>
    </ThemeProvider>
  </BrowserRouter>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
